<script lang="ts">
    import { onMount } from 'svelte'
    import { derived as sderived, writable } from 'svelte/store'
    import type { Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sgoto, spath } from 'hydria/svelte_helpers'

    import { Breadcrumb } from 'breadcrumb'

    type UserStatus = 'saving' | 'ready' | 'error'
    const status: Writable<UserStatus | null> = writable(null)

    let schema = yup.object().shape({
        email: yup.string().email().required(),
        name: yup.string(),
        org: yup.string(),
        welcome_link: yup.boolean().required(),
    })
    let error: string | undefined

    interface IForm {
        email: string
        name: string
        org: string
        welcome_link: boolean
    }

    let form: IForm = $state({
        email: '',
        name: '',
        org: '',
        welcome_link: true,
    })

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, form: any) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    let formIsValid = $derived(schema.isValidSync(form))
    let email_isValid = $derived(validateSyncAt('email', form))
    let name_isValid = $derived(validateSyncAt('name', form))
    let org_isValid = $derived(validateSyncAt('org', form))
    let welcome_link_isValid = $derived(validateSyncAt('welcome_link', form))

    $status = 'ready'

    async function createUser() {
        $status = 'saving'
        error = undefined

        const data: { [key: string]: any } = {
            email: form.email,
            active: true,
            send_welcome_email: form.welcome_link,
            metadata: {
                name: form.name !== '' ? form.name : null,
                org: form.org !== '' ? form.org : null,
            },
        }

        try {
            const r = await simpleFetch('POST', '/json/wdom-user/1', data)
            sgoto(`/wdom-user/${r.data.item.id}`)
        } catch (e: any) {
            console.error('Failed to create user:', e)

            if (e && e.data && e.data.ecode === 'EMAIL') {
                notify({
                    type: 'warning',
                    title: $_('Utilisateur'),
                    text: 'L’adresse courriel existe déjà dans le système.',
                })
                $status = 'ready'
                return
            }
            notify({
                type: 'danger',
                title: $_('Utilisateur'),
                text: 'U’utilisateur n’a pas pu être créé' + (e && e.data && e.data.error ? ` : ${e.data.error}` : '.'),
            })
            $status = 'error'
            error = e && e.data && e.data.error ? e.data.error : 'Unknown error'
            return
        }
    }

    let inputElement: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (inputElement) inputElement.focus()
    })

    const crumbs = sderived(status, ($status) => {
        const cs: CrumbArray = [{ txt: $_('Utilisateurs'), url: spath('/wdom-users') }]
        switch ($status) {
            case 'ready':
            case 'saving':
                cs.push({ txt: $_('Nouvel utilisateur') })
                break
            case null:
            case null:
                cs.push({ txt: '…' })
                break
            case 'error':
                cs.push({ txt: iconExclamationTriangle, html: true })
                break
        }

        return cs
    })
</script>

<Breadcrumb {crumbs} />

<div class="card">
    <div class="card-title text-primary">{$_('Create a new user')}</div>
    <div class="card-body" style="max-width: 800px;">
        {#if $status == 'error'}
            <div class="alert alert-danger">
                <Icon inline icon="fa:exclamation-triangle" />
                {$_('An error occured. Please try again in a moment.')}
            </div>
        {:else if $status == null}
            <div class="loading">
                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                Initialisation…
            </div>
        {:else}
            <form
                class="d-grid gap-3"
                novalidate
                onsubmit={(e) => {
                    e.preventDefault()
                    createUser()
                }}
            >
                <div class="row">
                    <label for="email" class="col-sm-3 col-form-label">{$_('Adresse courriel')}</label>
                    <div class="col-sm-7">
                        <input type="email" id="email" class="form-control" placeholder="aa@bb.cc" bind:value={form.email} bind:this={inputElement} class:is-invalid={!email_isValid} />
                    </div>
                </div>
                <div class="row">
                    <label for="org" class="col-sm-3 col-form-label">{$_('Organisation')}</label>
                    <div class="col-sm-7">
                        <input type="text" id="org" class="form-control" bind:value={form.org} class:is-invalid={!org_isValid} />
                    </div>
                </div>
                <div class="row">
                    <label for="name" class="col-sm-3 col-form-label">{$_('Nom complet')}</label>
                    <div class="col-sm-7">
                        <input type="text" id="name" class="form-control" bind:value={form.name} class:is-invalid={!name_isValid} />
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-7 offset-sm-3">
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" bind:checked={form.welcome_link} />
                                {$_('Envoyer un courriel de bienvenue avec un link de connexion')}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="offset-sm-3 col-sm-9">
                        {#if $status == 'ready'}
                            <button type="submit" class="btn btn-primary" disabled={!formIsValid}>{$_('Créer l’utilisateur')}</button>
                        {:else}
                            <button type="button" class="btn btn-info" disabled
                                ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {$_('Un moment…')}</button
                            >
                        {/if}
                    </div>
                </div>
            </form>
        {/if}
    </div>
</div>
